import { useEffect, useRef, useState } from 'react';
import type { CoverageEligibilityRecord } from '@features/patient-coverages/types';
import { CloseRounded } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import {
  type ManualEligibilityDraft,
  useManualEligibilityDraft,
} from '../hooks/useManualEligibilityDraft';
import { ELIG_TOKENS } from '../theme/readinessTokens';
import { FINANCIAL_FIELDS, firstInvalidFinancialInput } from '../utils/verificationFinancials';

import { type BenefitInputField, CoverageBenefitFields } from './CoverageBenefitFields';

interface Props {
  initialRecord: CoverageEligibilityRecord;
  coverageLabel: string;
  allowed: boolean;
  isSaving?: boolean;
  saveBlocked?: boolean;
  error?: string | null;
  comparison?: React.ReactNode;
  onClose: () => void;
  onSave: (draft: ManualEligibilityDraft) => Promise<void>;
}

/** Mount for each edit session. The parent owns command results and closing after success. */
export function ManualEligibilityDialog({
  initialRecord,
  coverageLabel,
  allowed,
  isSaving = false,
  saveBlocked = false,
  error,
  comparison,
  onClose,
  onSave,
}: Props) {
  const { eligibilityRecord: tracking } = useEventTracking();
  const trackedOpen = useRef(false);
  useEffect(() => {
    if (!trackedOpen.current) {
      trackedOpen.current = true;
      tracking.manualEntryOpened();
    }
  }, [tracking]);
  const close = () => {
    tracking.manualEntryClosed();
    onClose();
  };
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { draft, setDraft, changeBenefit } = useManualEligibilityDraft(initialRecord);
  const [validation, setValidation] = useState<string | null>(null);
  const [invalidBenefit, setInvalidBenefit] = useState<BenefitInputField>();
  const submitting = useRef(false);
  const [pending, setPending] = useState(false);
  const busy = isSaving || pending;
  const sessionCoverageId = useRef(initialRecord.patient_insurance_coverage_id).current;
  const sessionAllowed =
    allowed && sessionCoverageId === initialRecord.patient_insurance_coverage_id;
  const formRef = useRef<HTMLFormElement>(null);

  function update<K extends keyof ManualEligibilityDraft>(
    key: K,
    value: ManualEligibilityDraft[K],
  ): void {
    setDraft(previous => ({ ...previous, [key]: value }));
  }

  async function submit(event: React.SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (submitting.current || busy || !sessionAllowed || saveBlocked) return;
    const assertedInputs = Object.fromEntries(
      [...draft.assertedFields].map(field => [field, draft.financials[field]]),
    );
    // Number inputs can expose an empty value for malformed text (for example an
    // incomplete exponent). The browser validity flag distinguishes that from a clear.
    const malformedField = FINANCIAL_FIELDS.find(
      item =>
        draft.assertedFields.has(item.field) &&
        formRef.current?.querySelector<HTMLInputElement>(`input[name="benefit-${item.field}"]`)
          ?.validity.badInput,
    );
    const invalidAmount = malformedField?.label ?? firstInvalidFinancialInput(assertedInputs);
    const message = !draft.result
      ? 'Choose a coverage result.'
      : !draft.contactedAt || !Number.isFinite(new Date(draft.contactedAt).getTime())
        ? 'Enter a valid contact date and time.'
        : invalidAmount
          ? `Enter a valid, non-negative value for ${invalidAmount}.`
          : null;
    setValidation(message);
    setInvalidBenefit(
      invalidAmount
        ? FINANCIAL_FIELDS.find(item => item.label === invalidAmount)?.field
        : undefined,
    );
    if (message) {
      if (!draft.result)
        formRef.current?.querySelector<HTMLInputElement>('input[name="coverage-result"]')?.focus();
      else if (!draft.contactedAt || !Number.isFinite(new Date(draft.contactedAt).getTime())) {
        formRef.current?.querySelector<HTMLInputElement>('input[name="contact-time"]')?.focus();
      } else if (invalidAmount) {
        const field = FINANCIAL_FIELDS.find(item => item.label === invalidAmount)?.field;
        if (field)
          formRef.current
            ?.querySelector<HTMLInputElement>(`input[name="benefit-${field}"]`)
            ?.focus();
      }
      return;
    }
    tracking.manualEntrySubmitted();
    submitting.current = true;
    setPending(true);
    try {
      await onSave(draft);
    } catch {
      setValidation('Could not save benefits. Your entries are preserved. Please try again.');
    } finally {
      submitting.current = false;
      setPending(false);
    }
  }

  return (
    <Dialog
      open
      onClose={busy ? undefined : close}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      aria-labelledby="manual-eligibility-title"
      aria-describedby="manual-eligibility-description"
    >
      <Box
        component="form"
        ref={formRef}
        onSubmit={submit}
        noValidate
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          maxHeight: '100%',
          overflow: 'hidden',
        }}
      >
        <DialogTitle
          id="manual-eligibility-title"
          sx={{ pr: 7, pb: 1, fontSize: 18, fontWeight: 750 }}
        >
          {initialRecord.state === 'empty' ? 'Manually enter benefits' : 'Manually update benefits'}
          <IconButton
            aria-label="Close manual benefits"
            onClick={close}
            disabled={busy}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRounded />
          </IconButton>
        </DialogTitle>
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 12.5 }}>
            {coverageLabel}
          </Typography>
          <Typography
            id="manual-eligibility-description"
            sx={{ color: ELIG_TOKENS.inkSoft, fontSize: 12.5, mt: 0.5 }}
          >
            Enter the details you confirmed with the payer. This does not run an automatic check.
          </Typography>
        </Box>
        <DialogContent dividers>
          <Stack spacing={2.25} sx={{ py: 0.5 }}>
            {!sessionAllowed && (
              <Alert severity="info">Manual entry is unavailable. Your draft is preserved.</Alert>
            )}
            {comparison}
            <FormControl
              required
              error={!!validation && !draft.result}
              disabled={busy || !sessionAllowed}
            >
              <FormLabel
                id="coverage-result-label"
                sx={{ fontSize: 12.5, fontWeight: 750, mb: 1, color: ELIG_TOKENS.ink }}
              >
                Coverage result
              </FormLabel>
              <RadioGroup
                row
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  border: '1px solid',
                  borderColor: ELIG_TOKENS.lineStrong,
                  borderRadius: 1,
                  overflow: 'hidden',
                  '& .MuiFormControlLabel-root': {
                    m: 0,
                    px: 0.5,
                    py: 1.25,
                    justifyContent: 'center',
                    minWidth: 0,
                    borderRight: '1px solid',
                    borderColor: ELIG_TOKENS.lineStrong,
                    '&:last-child': { borderRight: 0 },
                    '&:has(input:checked)': {
                      bgcolor: ELIG_TOKENS.graySoft,
                      color: ELIG_TOKENS.midnight,
                    },
                    '&:has(input:focus-visible)': { outline: '2px solid', outlineOffset: -2 },
                  },
                  '& .MuiFormControlLabel-label': {
                    fontSize: 12.5,
                    fontWeight: 720,
                    textAlign: 'center',
                  },
                  '& .MuiRadio-root': { position: 'absolute', opacity: 0 },
                }}
                name="coverage-result"
                aria-labelledby="coverage-result-label"
                value={draft.result}
                onChange={event => {
                  const value = event.target.value;
                  if (value === 'active' || value === 'inactive' || value === 'unknown')
                    update('result', value);
                }}
              >
                <FormControlLabel value="active" control={<Radio />} label="Active" />
                <FormControlLabel value="inactive" control={<Radio />} label="Inactive" />
                <FormControlLabel value="unknown" control={<Radio />} label="Could not confirm" />
              </RadioGroup>
            </FormControl>
            <TextField
              sx={{ maxWidth: { sm: 260 } }}
              name="contact-time"
              label="Checked at"
              type="datetime-local"
              required
              fullWidth
              value={draft.contactedAt}
              onChange={event => update('contactedAt', event.target.value)}
              disabled={busy || !sessionAllowed}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Use the original contact time in your local time zone."
            />
            <FormControl disabled={busy || !sessionAllowed}>
              <FormLabel
                id="verification-method-label"
                sx={{ fontSize: 12.5, fontWeight: 750, color: ELIG_TOKENS.ink }}
              >
                How did you check?
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="verification-method-label"
                value={draft.method}
                onChange={event => {
                  const value = event.target.value;
                  if (value === 'phone' || value === 'portal') update('method', value);
                }}
              >
                <FormControlLabel value="portal" control={<Radio />} label="Payer portal" />
                <FormControlLabel value="phone" control={<Radio />} label="Phone" />
              </RadioGroup>
            </FormControl>
            {draft.method === 'phone' && (
              <TextField
                label="Representative name"
                value={draft.representativeName}
                onChange={event => update('representativeName', event.target.value)}
                disabled={busy || !sessionAllowed}
              />
            )}
            <CoverageBenefitFields
              invalidField={invalidBenefit}
              values={draft.financials}
              assertedFields={draft.assertedFields}
              onChange={changeBenefit}
              disabled={busy || !sessionAllowed}
            />
            <TextField
              label="Notes (optional)"
              multiline
              minRows={2}
              value={draft.notes}
              onChange={event => update('notes', event.target.value)}
              disabled={busy || !sessionAllowed}
            />
            <TextField
              label="Reference or confirmation number (optional)"
              value={draft.referenceNumber}
              onChange={event => update('referenceNumber', event.target.value)}
              disabled={busy || !sessionAllowed}
            />
            {(validation || error) && <Alert severity="error">{validation || error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !sessionAllowed || saveBlocked}
          >
            {busy ? 'Saving…' : 'Save benefits'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
